import { ApiPaths } from "../../../api/ApiPaths";
import { CareRecipientId } from "../../../../domain/care_recipient/CareRecipientId";
import { CareRecipientName } from "../../../../domain/care_recipient/CareRecipientName";
import { CareRecipientHouseId } from "../../../../domain/care_recipient/CareRecipientHouseId";
import { DateObject } from "../../../../domain/fundamental/DateObject";
import { ServiceProviderId } from "../../../../domain/service_provider/ServiceProviderId";
import { DateTime } from "../../../../domain/fundamental/DateTime";
import { Sensor } from "../../../../domain/care_recipient/Sensor";
import { ApiRequest } from "../../../api/ApiRequest";
import { CareRecipientClassification } from "../../../../domain/care_recipient/CareRecipientClassification";
import { DiseaseLabel } from "../../../../domain/care_recipient/Disease";

export type GetCareRecipientListSuccessResponseType = {
    careRecipients: {
        id: string,
        name: string,
        houseId: string,
        dataSyncStartTime: string | null,
        dataSyncEndTime: string | null,
        alertPeriod: {
            start: string,
            end: string
        } | null
    }[],
    // API処理結果判別用：OK->undefined、NG->ErrorResponseType
    error: undefined
};

export async function getCareRecipientList(): Promise<GetCareRecipientListSuccessResponseType> {
    return await ApiRequest.get(
        ApiPaths.admin.careRecipientList,
        {},
        () => {},
        () => {});
}

export type GetCareRecipientInfoSuccessResponseType = {
    id: string,
    name: string,
    houseId: string,
    birthday: string,
    serviceProviderId: string,
    dataSyncStartTime: string | null,
    dataSyncEndTime: string | null,
    sensors: [{
        sensor: string
    }],
    classification: string | null,
    diseases: DiseaseLabel[] | null,
    alertPeriod: {
        start: string,
        end: string
    } | null,
    alertPause: boolean | null,
    customerCode: string | null,
    branchCode: string | null,
    // API処理結果判別用：OK->undefined、NG->ErrorResponseType
    error: undefined
};

export async function getCareRecipientInfo(
    cardRecipientId: CareRecipientId,
): Promise<GetCareRecipientInfoSuccessResponseType> {
    return await ApiRequest.get(
        ApiPaths.admin.careRecipientInfo.replace(/{careRecipientId}/g, cardRecipientId.value),
        {},
        () => {},
        () => {});
}

export type UpdateCareRecipientRequestType = {
    name: CareRecipientName,
    houseId: CareRecipientHouseId,
    birthday: DateObject,
    serviceProviderId: ServiceProviderId,
    dataSyncStartTime: DateTime | null,
    dataSyncEndTime: DateTime | null,
    sensors: (Sensor | undefined)[],
    classification: CareRecipientClassification | null,
    alertPeriod: {
        start: DateObject,
        end: DateObject
    } | null,
    alertPause: boolean | null,
    customerCode: string | null,
    branchCode: string | null,
};

export type UpdateCareRecipientSuccessResponseType = {
    id: string,
    name: string,
    houseId: string,
    birthday: string,
    serviceProviderId: string,
    dataSyncStartTime: string | null,
    dataSyncEndTime: string | null,
    sensors: [{
        sensor: string
    }],
    classification: string | null,
    diseases: DiseaseLabel[] | null,
    alertPeriod: {
        start: string,
        end: string
    } | null,
    alertPause: boolean | null,
    customerCode: string | null,
    branchCode: string | null,
    // API処理結果判別用：OK->undefined、NG->ErrorResponseType
    error: {
        code: string,
        message: string
    } | undefined
};

export async function updateCareRecipient(
    cardRecipientId: CareRecipientId,
    body: UpdateCareRecipientRequestType
) : Promise<UpdateCareRecipientSuccessResponseType> {
    return await ApiRequest.post(
        ApiPaths.admin.careRecipientUpdate.replace(/{careRecipientId}/g, cardRecipientId.value),
        {},
        convertBody(body),
        () => {},
        () => {});
}

export async function registerCareRecipient(
    body: UpdateCareRecipientRequestType
) : Promise<UpdateCareRecipientSuccessResponseType> {
    return await ApiRequest.post(
        ApiPaths.admin.careRecipientRegister,
        {},
        convertBody(body),
        () => {},
        () => {});
}

export async function deleteCareRecipient(
    cardRecipientId: CareRecipientId
) {
    return await ApiRequest.delete(
        ApiPaths.admin.careRecipientDelete.replace(/{careRecipientId}/g, cardRecipientId.value),
        {},
        () => {},
        () => {});
}

const convertBody = (body: UpdateCareRecipientRequestType) => {
    return {
        name: body.name.value,
        houseId: body.houseId.value,
        birthday: body.birthday.format(),
        serviceProviderId: body.serviceProviderId.value,
        dataSyncStartTime: body.dataSyncStartTime?.format(),
        dataSyncEndTime: body.dataSyncEndTime?.format(),
        sensors: body.sensors.map(it => (
            { sensor: it?.value }
        )),
        classification: body.classification?.value,
        alertPeriodStart: body.alertPeriod?.start.format(),
        alertPeriodEnd: body.alertPeriod?.end.format(),
        alertPause: body.alertPause,
        customerCode: body.customerCode,
        branchCode: body.branchCode,
    };
};

type GetAlertsResponseType = {
    alerts: {
        careRecipient: {
            id: string,
            name: string,
            dataSyncInformation: {
                dataSyncPeriod: {
                    startTime: string | null,
                    endTime: string | null
                },
                houseId: string
            }
        },
        period: {
            start: string,
            end: string
        } | null,
        alert: {
            id: string,
            date: string,
            message: string,
            category: string,
            display: boolean,
            createdFrom: string,
        }
    }[]
    error: undefined
}

export async function getAllAlerts(): Promise<GetAlertsResponseType> {
    return await ApiRequest.get(
        ApiPaths.admin.allAlert,
        {},
        () => {},
        () => {}
    );
}

type UpdateMonthSuccessType = {
    monthSettingHistory: {
        careRecipientId: string,
        startDate: string,
        endDate: string,
        createdBy: string,
        createdAt: string
    }
    error: undefined
}

export async function updatePeriod(careRecipientId: string, startDate: string | null, endDate: string | null): Promise<UpdateMonthSuccessType> {
    return await ApiRequest.post(
        ApiPaths.admin.calculationMonth.replace(/\{careRecipientId}/g, careRecipientId),
        {},
        { startDate: startDate, endDate: endDate },
        () => {},
        () => {}
    );
}

export async function disableAlert(alertId: string) {
    await ApiRequest.post(
        ApiPaths.admin.disableAlert.replace(/\{alertId\}/g, alertId),
        {},
        {},
        () => {},
        () => {}
    );
}

export async function disableAlerts(alertIdList: string[]) {
    await ApiRequest.post(
        ApiPaths.admin.disableAlerts,
        {},
        { list: alertIdList },
        () => {},
        () => {}
    );
}

export async function toggleAlertDisplay(alertId: string) {
    await ApiRequest.post(
        ApiPaths.admin.toggleAlertDisplay.replace(/\{alertId\}/g, alertId),
        {},
        {},
        () => {},
        () => {}
    );
}
